import { isPlatformBrowser } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    Inject,
    LOCALE_ID,
    OnInit,
    PLATFORM_ID,
    Renderer2,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ProgressComponent } from '@components/ui/progress/progress.component';
import { ToastModule } from '@components/ui/toast/toast.module';
import { LOCALE_IDS } from '@constants/app.constant';
import { StoreModel } from '@models/store.model';
import { Store } from '@ngrx/store';
import { AuthService } from '@services/auth.service';
import { CommonService } from '@services/common.service';
import { DestroyService } from '@services/destroy.service';
import { DeviceService } from '@services/device.service';
import { ProgressService } from '@services/progress.service';
import { ToastService } from '@services/toast.service';
import { loadAirlineLabelAction } from '@store/actions/common.action';
import { AnimationOptions } from 'ngx-lottie';
import { NgProgress, NgProgressModule, NgProgressRef } from 'ngx-progressbar';
import {
    debounceTime,
    distinctUntilChanged,
    EMPTY,
    filter,
    finalize,
    fromEvent,
    map,
    switchMap,
    takeUntil,
    withLatestFrom,
} from 'rxjs';

import { SwUpdateService } from '@services/sw-update.service';
import { CookieService } from 'ngx-cookie-service';

@Component({
    selector: 'pn-root',
    standalone: true,
    imports: [RouterOutlet, NgProgressModule, ProgressComponent, ToastModule],
    providers: [DestroyService],
    template: `
        <ng-progress #progressBar id="bookingFit-progress" [spinner]="false"></ng-progress>
        <pn-progress />
        <pn-toast />

        @defer {
            <router-outlet />
            <!-- } @loading (minimum 2s) {
            <div class="h-full w-full transition-all duration-200 flex-center">
                <ng-lottie [options]="_options" class="block w-96 mb:w-full" />
            </div> -->
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
    public progressRef!: NgProgressRef;
    protected _options: AnimationOptions = {
        path: '/assets/animate-json/loading.json',
    };
    public updateLoading$ = this._progress.disabledUserClick.pipe(
        map((value) => {
            return value > 0;
        }),
        distinctUntilChanged()
    );
    private readonly _isBrowser = isPlatformBrowser(this._platformId);

    constructor(
        private readonly _deviceService: DeviceService,
        @Inject(PLATFORM_ID) private readonly _platformId: object,
        @Inject(LOCALE_ID) private readonly _localeId: string,
        private readonly _render: Renderer2,
        private readonly _ngProgress: NgProgress,
        private readonly _toast: ToastService,
        private readonly _router: Router,
        private readonly _auth: AuthService,
        private readonly _progress: ProgressService,
        private readonly _store: Store<StoreModel>,
        private readonly _version: SwUpdateService,
        private readonly _commonService: CommonService,
        private readonly _destroy: DestroyService,
        private readonly _cookie: CookieService
    ) {
        if (this._isBrowser) {
            this._cookie.set('locale', _localeId, {
                path: '/',
            });
            // load master data
            this._store.dispatch(loadAirlineLabelAction());
        }
        afterNextRender(() => {
            this._version.detectNewVersion();
            const mobileAction = this._deviceService.isMobile ? 'addClass' : 'removeClass';
            this._render[mobileAction](document.documentElement, 'mobile-device');
            _deviceService
                .isTabletCheck()
                .pipe(takeUntil(this._destroy))
                .subscribe((isTablet) => {
                    const tabletAction = isTablet ? 'addClass' : 'removeClass';
                    this._render[tabletAction](document.documentElement, 'tablet-device');
                });
            this._onWindowError();
        });
    }

    ngOnInit(): void {
        this.progressRef = this._ngProgress.ref('bookingFit-progress');
        this._updateCanonicalLink();
        this._authChange();
        this._progressChange();
        this._disableUserClick();
        this._commonService.detectUsingApp();
    }

    /**
     * Update canonical link
     *
     * @private
     * @memberof AppComponent
     */
    private _updateCanonicalLink() {
        if (!this._isBrowser) {
            return;
        }
        this._router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                distinctUntilChanged()
            )
            .subscribe(() => {
                this._commonService.updateCanonicalLink(location.origin + location.pathname);
                const createLink = (local: string) =>
                    `{origin}/{local}/{path}`
                        .replace('{origin}', location.origin)
                        .replace('{local}', local)
                        .replace('{path}', location.pathname);
                const links = Object.values(LOCALE_IDS).map((local) => ({
                    link: createLink(local),
                    hreflang: local,
                }));
                this._commonService.updateAlternateLink(links);
            });
    }

    /**
     * Auth change
     *
     * @private
     * @return {*}
     * @memberof AppComponent
     */
    private _authChange() {
        if (!this._isBrowser) {
            return;
        }
        this._auth.authState
            .pipe(
                withLatestFrom(this._auth.session),
                switchMap(([event, session]) => {
                    if (session) {
                        this._toast.warn(
                            $localize`:@@auth.buttons.sign-in:Đăng nhập`,
                            $localize`:@@auth.toast.already-logged-in:Bạn đã đăng nhập!`
                        );
                        return EMPTY;
                    }

                    this._progress.start({
                        id: 'google-auth',
                        message: $localize`:@@auth.sign-in.get-account-info:Đang lấy thông tin tài khoản...`,
                    });
                    return this._auth.getSessionByToken(event).pipe(
                        debounceTime(300),
                        finalize(() => this._progress.complete('google-auth'))
                    );
                })
            )
            .subscribe((session) => {
                if (session) {
                    this._auth.updateSession(session.UserInfo, session.SessionKey);
                }
            });
    }

    /**
     * Progress change
     *
     * @private
     * @return {*}
     * @memberof AppComponent
     */
    private _progressChange(): void {
        if (!this._isBrowser) {
            return;
        }
        this._progress.loading
            .pipe(
                map((loading) => loading > 0),
                distinctUntilChanged()
            )
            .subscribe((loading) => {
                const fn = loading ? 'start' : 'complete';
                this.progressRef[fn]();
            });
    }

    /**
     * Disable user click
     *
     * @private
     * @memberof AppComponent
     */
    private _disableUserClick() {
        if (!this._isBrowser) {
            return;
        }
        // disable user interaction when call api update or create
        fromEvent(document, 'click', { capture: true })
            .pipe(
                withLatestFrom(this.updateLoading$),
                filter(([_, loading]) => loading)
            )
            .subscribe(([event]) => {
                event.preventDefault();
                event.stopPropagation();
            });
    }

    /**
     * Window error
     *
     * @private
     * @return {*}
     * @memberof AppComponent
     */
    private _onWindowError() {
        if (!this._isBrowser) {
            return;
        }
        fromEvent<ErrorEvent>(window, 'error', { capture: true })
            .pipe(
                takeUntil(this._destroy),
                filter((e) => !!e.target && e.target instanceof HTMLScriptElement),
                map((e) => e.target as HTMLScriptElement)
            )
            .subscribe((target) => {
                console.error('Lỗi tải script:', target.src);
                if (target.src.endsWith('.js')) {
                    this._toast.error(
                        $localize`:@@app.window-error.title:Có lỗi xảy ra`,
                        $localize`:@@app.window-error.error-loading-page:Đã xảy ra lỗi! Có thể phiên bản hiện tại của ứng dụng không tương thích với hệ thống. Vui lòng tải lại trang hoặc liên hệ quản trị viên để được hỗ trợ.`,
                        {
                            closeOnNavigation: true,
                            id: 'error-loading-page',
                        }
                    );
                }
            });
    }
}
